import moment from 'moment';
import {TFunction} from 'i18next';
import {CarePlanConfigurationFeature} from '../../../../../backend/enums/care-plan-configuration-feature';
import {PosologyScheduleFormType} from '../../../../../types/schedule';
import {scheduleReadableExpression} from '../../../../functions/schedule-readable-expression';
import {safeParseJson} from '../../../../functions/safe-parse-json';
import {translations} from '../../../../../i18n/translations';
import {
  ExportCarePlanData,
  ExportCarePlanEntry,
  ExportCarePlanTableColumnStyle,
  ExportCarePlanTableData,
} from './export-care-plan-types';

export class ExportCarePlanGroupDataProcessor {
  readonly baseData: ExportCarePlanData;
  readonly t: TFunction;

  constructor(baseData: ExportCarePlanData, t: TFunction) {
    this.baseData = baseData;
    this.t = t;
  }

  getEntryByFeature(
    entries: ExportCarePlanEntry[],
    feature: CarePlanConfigurationFeature
  ): ExportCarePlanEntry | undefined {
    return entries.find((entry) => entry.feature === feature);
  }

  getEntryById(entries: ExportCarePlanEntry[], id: string): ExportCarePlanEntry | undefined {
    return entries.find((entry) => entry.id === id);
  }

  getTaskEntryById(id: string | string[]): ExportCarePlanEntry | undefined {
    const entryIds = Array.isArray(id) ? id : [id];

    for (const taskEntries of this.baseData.tasks) {
      for (const entryId of entryIds) {
        const entry = this.getEntryById(taskEntries, entryId);

        if (entry) {
          return entry;
        }
      }
    }

    return undefined;
  }

  getEntryValue(entry?: ExportCarePlanEntry): string {
    if (!entry) return '';
    return Array.isArray(entry.value) ? entry.value.join(', ') : (entry.value ?? '');
  }

  /** Resolve the column headers for the tasks table. */
  resolveTaskHeaders(firstRowData: RowData): Promise<string[]> | string[] {
    return firstRowData.entries.map((entry) => entry.name);
  }

  /** Resolve one row for each task. Called for each row. */
  resolveTaskRow(rowData: RowData): Promise<string[]> | string[] {
    return rowData.entries.map((entry) => this.getEntryValue(entry));
  }

  /** Resolve the column styles for the tasks table. */
  resolveTasksColumnStyles(taskHeaders: string[]): ExportCarePlanTableColumnStyle | undefined {
    return undefined;
  }

  /** Resolve the column headers for the details table. */
  resolveDetailsHeaders(firstRowData: RowData): string[] {
    return firstRowData.entries.map((entry) => entry.name);
  }

  /**  Resolve the column headers for the details table. */
  resolveDetailsRow(rowData: RowData): Promise<string[]> | string[] {
    return rowData.entries.map((entry) => this.getEntryValue(entry));
  }

  /** Resolve the column styles for the details table. */
  resolveDetailsColumnStyles(detailsHeaders: string[]): ExportCarePlanTableColumnStyle | undefined {
    return undefined;
  }

  resolveEntryCell(entry: ExportCarePlanEntry): string {
    switch (entry.feature) {
      case CarePlanConfigurationFeature.StartsAtTime:
      case CarePlanConfigurationFeature.EndsAtTime:
        return entry.value ? moment(entry.value).format('HH:mm') : '';

      case CarePlanConfigurationFeature.StartsAtDate:
        return entry.value ? moment(entry.value).format('DD/MM/YYYY') : '';

      default:
        return this.getEntryValue(entry);
    }
  }

  reorderCells(items: string[], endDate: string, endHour: string): string[] {
    return [
      items[0],
      items[2],
      items[1],
      items[6],
      endDate,
      endHour,
      items[4],
      ...items.filter((_, index) => ![0, 2, 1, 6, 4].includes(index)),
    ];
  }

  resolveEntryCellSchedule(entry: ExportCarePlanEntry): {
    periodicity: string;
    endDate: string;
    endHour: string;
  } {
    const schedule: PosologyScheduleFormType | undefined = safeParseJson(entry.value);
    const dateExpression = schedule
      ? scheduleReadableExpression({t: this.t, scheduleDefinition: schedule})
      : undefined;
    return {
      periodicity: dateExpression ?? this.t(translations.export.no_repetition),
      endDate: schedule?.endDate ? moment(schedule.endDate).format('DD/MM/YYYY') : '',
      endHour: schedule?.endHour ? moment(schedule.endHour).format('HH:mm') : '',
    };
  }

  /** Main method to call to process data. Returns the tasks table ready for PDF rendering. */
  async process(): Promise<ExportCarePlanTableData> {
    const firstRowData = new RowData(this.baseData.tasks[0] ?? []);

    const title = this.baseData.group.name;
    const taskHeaders = await this.resolveTaskHeaders(firstRowData);
    const taskRows = await Promise.all(
      this.baseData.tasks.map((entries) => {
        const rowData = new RowData(entries);
        return this.resolveTaskRow(rowData);
      })
    );

    const detailsHeaders = this.resolveDetailsHeaders(new RowData(this.baseData.details));
    const detailsRows = await this.resolveDetailsRow(new RowData(this.baseData.details)); // call once to trigger any async logic, even if we don't use the result here

    const detailsColumnStyles = this.resolveDetailsColumnStyles(detailsHeaders);
    const tasksColumnStyles = this.resolveTasksColumnStyles(taskHeaders);

    const result: ExportCarePlanTableData = {
      title: this.t(translations.screen_paths.senior_care_plan) + ' > ' + title,
      tasks: {
        title: title,
        headers: taskHeaders,
        rows: taskRows,
        columnStyles: tasksColumnStyles,
      },
      details: {
        title: this.t(translations.export.details),
        headers: detailsHeaders,
        rows: [detailsRows],
        columnStyles: detailsColumnStyles,
      },
    };

    return result;
  }
}

export class RowData {
  constructor(readonly entries: ExportCarePlanEntry[]) {}

  getEntryByFeature(feature: CarePlanConfigurationFeature): ExportCarePlanEntry | undefined {
    return this.entries.find((entry) => entry.feature === feature);
  }

  getEntryById(id: string): ExportCarePlanEntry | undefined {
    return this.entries.find((entry) => entry.id === id);
  }
}
